import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { IPspDefinition, IPspLoginInfoItem, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Popover, PopoverModule } from 'primeng/popover';
import { Clipboard } from '@angular/cdk/clipboard';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-psp-definition',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        ConfirmPopupModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ToggleSwitch,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PopoverModule,
        PageHeaderComponent
    ],
    templateUrl: './psp-definition.component.html',
    styleUrl: './psp-definition.component.scss'
})
export class PspDefinitionComponent implements OnInit {
    @ViewChild('dt') dt!: Table;
    @ViewChild('loginPopover') loginPopover!: Popover;

    pspDefinitions: IPspDefinition[] = [];
    tenantTypes: ITypeListItem[] = [];
    serviceProviders: string[] = [];
    paymentMethods: string[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedPsp: IPspDefinition | null = null;
    submitted = false;
    pspForm!: FormGroup;
    loginForm!: FormGroup;
    activeLoginInfo: IPspLoginInfoItem | null = null;
    private popoverHideTimeout: any = null;

    expandedRows: any = {};
    loginDialogVisible = false;
    isLoginEditMode = false;
    selectedLoginInfo: IPspLoginInfoItem | null = null;
    loginSubmitted = false;
    currentPspId: number | null = null;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private clipboard = inject(Clipboard);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.initializeLoginForm();
        this.loadPspDefinitions();
        this.loadDropdowns();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    onRowExpand(event: any): void {
        const id = event.data.pspType?.id;
        if (id) {
            this.expandedRows[id] = true;
        }
    }

    onRowCollapse(event: any): void {
        const id = event.data.pspType?.id;
        if (id) {
            delete this.expandedRows[id];
        }
    }

    loadPspDefinitions(): void {
        this.loading = true;
        this.paymentService.getPspDefinitions().subscribe({
            next: (res) => {
                this.pspDefinitions = res.pspDefinitions || [];
                // Update expanded rows data if needed
                if (Object.keys(this.expandedRows).length > 0) {
                    const newExpandedRows: { [key: string]: boolean } = {};
                    this.pspDefinitions.forEach((psp) => {
                        if (this.expandedRows[psp.pspType.id]) {
                            newExpandedRows[psp.pspType.id] = true;
                        }
                    });
                    this.expandedRows = newExpandedRows;
                }
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadDropdowns(): void {
        this.paymentService.getTenantTypeInformation().subscribe({
            next: (res) => {
                this.tenantTypes = (res.typeList || []).filter((t) => t.isActive);
            }
        });
        this.paymentService.getServiceProviders().subscribe({
            next: (res) => {
                this.serviceProviders = res || [];
            }
        });
        this.paymentService.getPaymentMethods().subscribe({
            next: (res) => {
                this.paymentMethods = res || [];
            }
        });
    }

    initializeForm(): void {
        this.pspForm = this.fb.group({
            text: ['', [Validators.required]],
            providerType: [null, [Validators.required]],
            paymentMethod: [null, [Validators.required]],
            tenantId: [null, [Validators.required]],
            active: [true]
        });
    }

    initializeLoginForm(): void {
        this.loginForm = this.fb.group({
            deepLink: ['', [Validators.required]],
            userName: ['', [Validators.required]],
            password: ['', [Validators.required]],
            additionalInfo: ['']
        });
    }

    isInvalid(field: string): boolean {
        const control = this.pspForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    isLoginInvalid(field: string): boolean {
        const control = this.loginForm.get(field);
        return !!(control && control.invalid && (this.loginSubmitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedPsp = null;
        this.submitted = false;
        this.pspForm.reset({ text: '', providerType: null, paymentMethod: null, tenantId: null, active: true });
        this.dialogVisible = true;
    }

    openEdit(psp: IPspDefinition): void {
        this.isEditMode = true;
        this.selectedPsp = psp;
        this.submitted = false;
        this.pspForm.patchValue({
            text: psp.pspType.text,
            providerType: psp.pspType.type,
            paymentMethod: psp.pspType.method,
            tenantId: psp.pspType.tenant.id,
            active: psp.pspType.active
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    openLoginCreate(pspId: number): void {
        this.isLoginEditMode = false;
        this.selectedLoginInfo = null;
        this.loginSubmitted = false;
        this.currentPspId = pspId;
        this.loginForm.reset({ deepLink: '', userName: '', password: '', additionalInfo: '' });
        this.loginDialogVisible = true;
    }

    openLoginEdit(info: IPspLoginInfoItem, pspId: number): void {
        this.isLoginEditMode = true;
        this.selectedLoginInfo = info;
        this.loginSubmitted = false;
        this.currentPspId = pspId;
        this.loginForm.patchValue({
            deepLink: info.deepLink,
            userName: info.userName,
            password: info.password,
            additionalInfo: info.additionalInfo
        });
        this.loginDialogVisible = true;
    }

    confirmLoginDelete(event: Event, info: IPspLoginInfoItem): void {
        this.confirmationService.confirm({
            target: event.target as HTMLElement,
            message: 'Are you sure you want to delete this login info?',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: { severity: 'danger' },
            accept: () => this.onLoginDelete(info)
        });
    }

    onLoginDelete(info: IPspLoginInfoItem): void {
        if (!info?.id) return;
        this.paymentService.deleteLoginInfo(info.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Login info has been deleted successfully.' });
                this.loadPspDefinitions();
            }
        });
    }

    cancelLoginDialog(): void {
        this.loginDialogVisible = false;
    }

    saveLoginInfo(): void {
        this.loginSubmitted = true;
        if (this.loginForm.invalid || !this.currentPspId) {
            return;
        }

        const formData = this.loginForm.value;
        const request = {
            pspId: this.currentPspId,
            deepLink: formData.deepLink,
            userName: formData.userName,
            password: formData.password,
            additionalInfo: formData.additionalInfo
        };

        if (!this.isLoginEditMode) {
            this.paymentService.createLoginInfo(request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Login info has been created successfully.'
                    });
                    this.loadPspDefinitions();
                    this.loginDialogVisible = false;
                }
            });
        } else if (this.selectedLoginInfo && this.selectedLoginInfo.id) {
            this.paymentService.updateLoginInfo(this.selectedLoginInfo.id, request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Login info has been updated successfully.'
                    });
                    this.loadPspDefinitions();
                    this.loginDialogVisible = false;
                }
            });
        }
    }

    showLoginPopover(event: MouseEvent, row: IPspDefinition): void {
        if (this.popoverHideTimeout) {
            clearTimeout(this.popoverHideTimeout);
            this.popoverHideTimeout = null;
        }
        this.activeLoginInfo = row.loginInfo?.[0] || null;
        this.loginPopover.toggle(event);
    }

    hideLoginPopover(): void {
        this.popoverHideTimeout = setTimeout(() => {
            this.loginPopover.hide();
        }, 200);
    }

    cancelHidePopover(): void {
        if (this.popoverHideTimeout) {
            clearTimeout(this.popoverHideTimeout);
            this.popoverHideTimeout = null;
        }
    }

    copyToClipboard(value: string | undefined): void {
        if (value) {
            this.clipboard.copy(value);
            this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Copied to clipboard', life: 2000 });
        }
    }

    savePsp(): void {
        this.submitted = true;
        if (this.pspForm.invalid) {
            return;
        }

        const formData = this.pspForm.value;

        if (!this.isEditMode) {
            this.paymentService
                .createPsp({
                    text: formData.text,
                    providerType: formData.providerType,
                    paymentMethod: formData.paymentMethod,
                    tenantId: formData.tenantId
                })
                .subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PSP has been created successfully.' });
                        this.loadPspDefinitions();
                        this.dialogVisible = false;
                    }
                });
        } else if (this.selectedPsp) {
            this.paymentService
                .updatePsp(this.selectedPsp.pspType.id, {
                    text: formData.text,
                    providerType: formData.providerType,
                    paymentMethod: formData.paymentMethod,
                    tenantId: formData.tenantId,
                    active: formData.active
                })
                .subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'PSP has been updated successfully.' });
                        this.loadPspDefinitions();
                        this.dialogVisible = false;
                    }
                });
        }
    }
}
